import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import {
  createOwner,
  deleteOwner,
  resolveEsiEntities,
  type EsiEntityCandidate,
} from "../api/chains";
import type { System, SystemOwner } from "../types/chain";
import { EntityLogo } from "./EntityLogo";

interface OwnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chainId: string;
  system: System;
  updateSystem: (id: string, patch: Partial<System>) => void;
  editing?: SystemOwner;
}

export function OwnerDialog({
  open,
  onOpenChange,
  chainId,
  system,
  updateSystem,
  editing,
}: OwnerDialogProps) {
  const [type, setType] = useState<"corp" | "alliance">(
    editing?.entity_type ?? "corp",
  );
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<EsiEntityCandidate[] | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async () => {
    const q = query.trim();
    if (q.length < 3) return;
    setBusy(true);
    setError(null);
    try {
      const esiType = type === "corp" ? "corporation" : "alliance";
      setCandidates(await resolveEsiEntities(esiType, q));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed.");
      setCandidates(null);
    } finally {
      setBusy(false);
    }
  };

  const pick = async (candidate: EsiEntityCandidate) => {
    if (
      editing &&
      editing.entity_type === type &&
      editing.entity_id === candidate.id
    ) {
      onOpenChange(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await createOwner(chainId, system.id, {
        entity_type: type,
        entity_id: candidate.id,
      });
      if (editing) {
        try {
          await deleteOwner(
            chainId,
            system.id,
            editing.entity_type,
            editing.entity_id,
          );
        } catch (deleteErr) {
          await deleteOwner(
            chainId,
            system.id,
            created.entity_type,
            created.entity_id,
          ).catch(() => {});
          throw deleteErr;
        }
      }
      let owners = (system.owners ?? []).filter(
        (r) =>
          !(
            r.entity_type === created.entity_type &&
            r.entity_id === created.entity_id
          ),
      );
      if (editing) {
        owners = owners.filter(
          (r) =>
            !(
              r.entity_type === editing.entity_type &&
              r.entity_id === editing.entity_id
            ),
        );
      }
      updateSystem(system.id, { owners: [...owners, created] });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save owner.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal__overlay" />
        <Dialog.Content className="modal__content" aria-describedby={undefined}>
          <Dialog.Title className="modal__title">
            {editing ? "Change owner" : "Add owner"}
          </Dialog.Title>
          <div className="system-panel__add-row">
            <select
              className="form__input"
              value={type}
              onChange={(e) => {
                setType(e.target.value as "corp" | "alliance");
                setCandidates(null);
              }}
            >
              <option value="corp">Corp</option>
              <option value="alliance">Alliance</option>
            </select>
            <input
              className="form__input"
              placeholder="Search by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void search();
                }
              }}
            />
            <button
              type="button"
              className="form__btn"
              onClick={search}
              disabled={busy || query.trim().length < 3}
            >
              {busy ? "…" : "Search"}
            </button>
          </div>
          {query.trim().length < 3 && (
            <p className="form__hint">Type at least 3 characters</p>
          )}
          {candidates && candidates.length === 0 && (
            <p className="system-panel__empty-line">No matches.</p>
          )}
          {candidates && candidates.length > 0 && (
            <ul className="system-panel__candidates">
              {candidates.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="system-panel__candidate"
                    onClick={() => pick(c)}
                    disabled={busy}
                  >
                    <EntityLogo type={type} id={c.id} />{" "}
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {error && (
            <p className="form__hint form__hint--error" role="alert">
              {error}
            </p>
          )}
          <div className="modal__actions">
            <button
              type="button"
              className="modal__btn"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
